import React, { Ref } from 'react';

import { useInView } from 'react-intersection-observer';

type AsyncCallback = () => Promise<void>;
export type UseLoadMoreData = {
  onLoad: AsyncCallback;
  onRetry?: AsyncCallback;
  isAuto?: boolean;
  hasError?: boolean;
  isLoading?: boolean;
};

const useLoadMoreData = <TElement extends HTMLElement>({
  isAuto,
  onLoad: onLoadProp,
  onRetry: onRetryProp,
  ...props
}: UseLoadMoreData): {
  onLoad: AsyncCallback;
  onRetry: AsyncCallback;
  hasError: boolean;
  isLoading: boolean;
  inViewRef: Ref<TElement>;
} => {
  const [internalIsLoading, setInternalIsLoading] = React.useState(false);
  const isLoading =
    typeof props.isLoading === 'boolean' ? props.isLoading : internalIsLoading;
  const [internalHasError, setInternalHasError] = React.useState(false);
  const hasError =
    typeof props.hasError === 'boolean' ? props.hasError : internalHasError;
  const [inViewRef, inView] = useInView();

  const onLoad = React.useCallback(() => {
    setInternalIsLoading(true);
    setInternalHasError(false);
    return onLoadProp()
      .catch(() => setInternalHasError(true))
      .finally(() => setInternalIsLoading(false));
  }, [onLoadProp, setInternalIsLoading, setInternalHasError]);

  const onRetry = React.useCallback(() => {
    setInternalIsLoading(true);
    setInternalHasError(false);
    const caller = onRetryProp || onLoadProp;
    return caller()
      .catch(() => setInternalHasError(true))
      .finally(() => setInternalIsLoading(false));
  }, [onRetryProp, onLoadProp, setInternalIsLoading, setInternalHasError]);

  React.useEffect(() => {
    if (inView && isAuto) {
      (async function () {
        await onLoad();
      })();
    }
  }, [inView, isAuto, onLoad]);

  return { inViewRef, isLoading, hasError, onLoad, onRetry };
};

export default useLoadMoreData;
